/**
 * Job Hunter — one agent at a glance: what it hunts, with which CV, what it has
 * found and its last run. Compact variant for the Overview strip.
 */
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { Bot, Copy, FileText, LoaderCircle, Pencil, Play, Trash2 } from 'lucide-react';
import type { JhAgent } from '@/services/job-hunter.service';
import { agentHex, agentSoft, num, scoreTone, timeAgo } from '@/lib/job-hunter/format';
import { Flags, Meter, Pill, Toggle } from './ui';
import { JH } from './JobHunterShell';
import { Button } from '@/components/Button';

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="min-w-0 rounded-xl bg-surface-alt/70 px-2.5 py-2">
      <div className="truncate text-[10px] font-semibold uppercase tracking-[0.04em] text-text-muted" title={label}>{label}</div>
      <div className={clsx('mt-0.5 text-[17px] font-bold tabular-nums tracking-tight', tone)}>{num(value)}</div>
    </div>
  );
}

export function CvChip({ agent, profileUid }: { agent: JhAgent; profileUid: string }) {
  if (!agent.cv) return <Link to={JH.candidate(profileUid, 'cvs')} className="jh-chip !bg-warning-soft !text-warning"><FileText size={11} />No CV — add one</Link>;
  const score = agent.cv.healthScore;
  return (
    <Link to={JH.candidate(profileUid, 'cvs')} className="jh-chip max-w-full hover:!bg-primary-soft hover:!text-primary" title={agent.cv.isDefault ? 'Uses the candidate’s default CV' : 'CV picked for this agent'}>
      <FileText size={11} className="shrink-0" /><span className="truncate">{agent.cv.label}</span>
      {agent.cv.isDefault && <span className="text-text-muted">· default</span>}
      {score != null && <span className="font-bold" style={{ color: scoreTone(score).color }}>{score}</span>}
    </Link>
  );
}

export function AgentCard({ agent, profileUid, running, onRun, onEdit, onDuplicate, onDelete, onToggle, compact }: {
  agent: JhAgent;
  profileUid: string;
  running?: boolean;
  onRun: () => void;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onToggle?: (active: boolean) => void;
  compact?: boolean;
}) {
  const hex = agentHex(agent.color);
  const s = agent.stats ?? { matches: 0, open: 0, applied: 0, queued: 0, scoredToday: 0 };
  const run = agent.lastRun;
  return (
    <article className={clsx('jh-card relative flex flex-col overflow-hidden', !agent.active && 'opacity-75')}>
      <span className="absolute inset-x-0 top-0 h-[3px]" style={{ background: hex }} />
      <div className={clsx('flex flex-col gap-3', compact ? 'p-3.5' : 'p-4')}>
        <div className="flex items-start gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl" style={{ background: agentSoft(agent.color, 18), color: hex }}><Bot size={19} /></span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="truncate text-[14.5px] font-bold tracking-tight">{agent.name}</h3>
              {!agent.active && <Pill tone="neutral">Paused</Pill>}
            </div>
            <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1.5"><CvChip agent={agent} profileUid={profileUid} /></div>
          </div>
          {onToggle && <Toggle checked={agent.active} onChange={onToggle} label={agent.active ? 'Pause agent' : 'Activate agent'} />}
        </div>

        <div className="flex flex-col gap-1.5">
          <Flags codes={agent.targetCountries} max={compact ? 5 : 8} />
          {!compact && (
            <div className="flex flex-wrap gap-1">
              {agent.roleKeywords.slice(0, 6).map((k) => <span key={k} className="jh-chip !text-[10.5px]">{k}</span>)}
              {agent.roleKeywords.length > 6 && <span className="jh-chip !text-[10.5px]">+{agent.roleKeywords.length - 6}</span>}
            </div>
          )}
        </div>

        {/* Two columns on a narrow card (phones), four once the card is wide enough for the labels. */}
        <div className="@container"><div className="grid grid-cols-2 gap-1.5 @[300px]:grid-cols-4">
          <Stat label="Found" value={s.matches} />
          <Stat label="Open" value={s.open} tone={s.open ? 'text-primary' : undefined} />
          <Stat label="Applied" value={s.applied} tone={s.applied ? 'text-success' : undefined} />
          <Stat label="Queued" value={s.queued} tone={s.queued ? 'text-warning' : undefined} />
        </div></div>

        <div>
          <div className="mb-1 flex justify-between text-[11.5px]"><span className="text-text-muted">AI scorings today</span><span className="font-semibold tabular-nums">{s.scoredToday} / {agent.dailyScoreLimit}</span></div>
          <Meter value={s.scoredToday} max={agent.dailyScoreLimit} />
        </div>

        <div className="text-[11.5px] text-text-muted">
          {run ? <>Last run {timeAgo(run.at)} · screened {num(run.screened)} · scored {num(run.scored)} · <b className="text-text-primary">+{run.proposed} match{run.proposed === 1 ? '' : 'es'}</b></> : 'Not run yet.'}
          {!compact && <span> · fit ≥ {agent.minScore}{agent.requireSponsorship ? ' · sponsorship only' : ''}</span>}
        </div>
      </div>

      <div className={clsx('mt-auto flex items-center gap-1.5 border-t border-border bg-surface-alt/40', compact ? 'px-3.5 py-2' : 'px-4 py-2.5')}>
        <Button size="sm" onClick={onRun} disabled={running} iconLeft={running ? <LoaderCircle size={13} className="animate-spin" /> : <Play size={13} />}>{running ? 'Running…' : 'Run'}</Button>
        <span className="flex-1" />
        {onEdit && <button type="button" title="Edit" onClick={onEdit} className="grid h-8 w-8 place-items-center rounded-lg border border-border text-text-secondary hover:border-primary hover:text-primary"><Pencil size={14} /></button>}
        {onDuplicate && <button type="button" title="Duplicate" onClick={onDuplicate} className="grid h-8 w-8 place-items-center rounded-lg border border-border text-text-secondary hover:border-primary hover:text-primary"><Copy size={14} /></button>}
        {onDelete && <button type="button" title="Delete" onClick={onDelete} className="grid h-8 w-8 place-items-center rounded-lg border border-border text-text-muted hover:border-danger hover:text-danger"><Trash2 size={14} /></button>}
      </div>
    </article>
  );
}
